import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Search, Users, TrendingUp, DollarSign } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function AthleteMarketplace() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    sport: "",
    gender: "",
    minEngagement: "",
    maxBudget: "",
    search: "",
  });

  const { data: athletes, isLoading } = useQuery({
    queryKey: ["athlete-marketplace", filters],
    queryFn: async () => {
      let query = supabase
        .from("athlete_marketplace" as any)
        .select("*, athletes(*)")
        .eq("is_available", true);

      const { data, error } = await query;

      if (error) throw error;

      // Filter in memory (in production, use database filters)
      let filtered = data || [];

      if (filters.sport) {
        filtered = filtered.filter((a: any) => 
          a.athletes.sport.toLowerCase().includes(filters.sport.toLowerCase())
        );
      }

      if (filters.gender) {
        filtered = filtered.filter((a: any) => a.athletes.gender === filters.gender);
      }

      if (filters.search) {
        filtered = filtered.filter((a: any) =>
          a.athletes.name.toLowerCase().includes(filters.search.toLowerCase())
        );
      }

      if (filters.minEngagement) {
        filtered = filtered.filter((a: any) =>
          (a.engagement_rate || 0) >= parseFloat(filters.minEngagement)
        );
      }

      if (filters.maxBudget) {
        filtered = filtered.filter((a: any) =>
          (a.min_deal_value || 0) <= parseFloat(filters.maxBudget)
        );
      }

      return filtered;
    },
  });

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold">Athlete Marketplace</h1>
          <p className="text-muted-foreground mt-1">
            Discover and connect with student-athletes
          </p>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Search Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="search"
                    placeholder="Athlete name..."
                    className="pl-8"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sport">Sport</Label>
                <Input
                  id="sport"
                  placeholder="e.g. Football"
                  value={filters.sport}
                  onChange={(e) => setFilters({ ...filters, sport: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gender">Gender</Label>
                <Select
                  value={filters.gender}
                  onValueChange={(value) => setFilters({ ...filters, gender: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="engagement">Min Engagement %</Label>
                <Input
                  id="engagement"
                  type="number"
                  placeholder="0"
                  value={filters.minEngagement}
                  onChange={(e) => setFilters({ ...filters, minEngagement: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="budget">Max Budget</Label>
                <Input
                  id="budget"
                  type="number"
                  placeholder="10000"
                  value={filters.maxBudget}
                  onChange={(e) => setFilters({ ...filters, maxBudget: e.target.value })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {athletes && athletes.length > 0 ? (
              athletes.map((athlete: any) => (
                <Card key={athlete.id} className="hover:border-primary/50 transition-colors">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <Avatar className="h-16 w-16">
                        <AvatarImage src={athlete.profile_photo_url} />
                        <AvatarFallback>
                          {athlete.athletes.name.split(' ').map((n: string) => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <CardTitle className="text-lg">{athlete.athletes.name}</CardTitle>
                        <CardDescription>
                          {athlete.athletes.sport} • {athlete.athletes.year}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {athlete.bio || "No bio available"}
                    </p>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                        <span>{athlete.engagement_rate || 0}% engagement</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span>Min: ${athlete.min_deal_value || 0}</span>
                      </div>
                    </div>

                    {athlete.preferred_categories && athlete.preferred_categories.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {athlete.preferred_categories.slice(0, 3).map((cat: string) => (
                          <Badge key={cat} variant="outline" className="text-xs">
                            {cat}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <Button
                      className="w-full"
                      onClick={() => navigate(`/brand/athlete/${athlete.athlete_id}`)}
                    >
                      View Profile
                    </Button>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="col-span-full">
                <CardContent className="py-12">
                  <div className="text-center text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No athletes found matching your filters</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
